// v3.15-926: Appointment details modal — reschedule, status change, notes.
// Opened by clicking an appointment in the Calendar page or the dashboard
// widget. Honors backend conflict detection (admin override via force flag).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Loader2, Clock, User, Phone, Mail, Trash2, ShieldAlert, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_OPTIONS = [
  { value: 'scheduled',   label: 'Scheduled',   bg: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'confirmed',   label: 'Confirmed',   bg: 'bg-green-50 text-green-700 border-green-200' },
  { value: 'completed',   label: 'Completed',   bg: 'bg-slate-100 text-slate-700 border-slate-300' },
  { value: 'no_show',     label: 'No Show',     bg: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'rescheduled', label: 'Rescheduled', bg: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'cancelled',   label: 'Cancelled',   bg: 'bg-slate-50 text-slate-500 border-slate-200' },
];

// v3.15-928: friendly timezone labels (kept in sync with InternalCalendarPage).
const TZ_LABELS = {
  'America/New_York':    'Eastern Time',
  'America/Chicago':     'Central Time',
  'America/Denver':      'Mountain Time',
  'America/Phoenix':     'Arizona Time',
  'America/Los_Angeles': 'Pacific Time',
  'America/Anchorage':   'Alaska Time',
  'Pacific/Honolulu':    'Hawaii Time',
  'UTC':                 'UTC',
};

const toLocalDT = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function AppointmentDetailsModal({ open, onOpenChange, appointment, onUpdated }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('scheduled');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt,   setEndsAt]   = useState('');
  const [notes,    setNotes]    = useState('');
  const [saving,   setSaving]   = useState(false);
  const [repTz,    setRepTz]    = useState('America/New_York');  // v3.15-928

  useEffect(() => {
    if (!open || !appointment) return;
    setStatus(appointment.status || 'scheduled');
    setStartsAt(toLocalDT(appointment.date_time_start));
    setEndsAt(toLocalDT(appointment.date_time_end));
    setNotes(appointment.notes || '');
    // v3.15-928: fetch rep timezone for display label.
    api.get('/appointments/me/settings')
      .then(({ data }) => { if (data?.settings?.timezone) setRepTz(data.settings.timezone); })
      .catch(() => {});
  }, [open, appointment]);

  const tzLabel = TZ_LABELS[repTz] || repTz;
  const whenDisplay = appointment?.date_time_start
    ? (() => {
        try {
          return new Date(appointment.date_time_start).toLocaleString('en-US', {
            weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true, timeZone: repTz,
          });
        } catch { return ''; }
      })()
    : '';

  const save = async () => {
    if (!appointment?.id) return;
    setSaving(true);
    try {
      const body = { status, notes };
      if (startsAt && startsAt !== toLocalDT(appointment.date_time_start)) {
        body.date_time_start = new Date(startsAt).toISOString();
      }
      if (endsAt && endsAt !== toLocalDT(appointment.date_time_end)) {
        body.date_time_end = new Date(endsAt).toISOString();
      }
      const { data } = await api.patch(`/appointments/${appointment.id}`, body);
      toast.success('Appointment updated');
      // v3.21-1788 · Ghost live-refresh.
      try { window.dispatchEvent(new CustomEvent('ghost:refresh', { detail: { reason: 'appointment_updated' } })); } catch (_e) { /* noop */ }
      onUpdated?.(data);
      onOpenChange(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.admin_force_required) {
        toast.error('Conflicts with another appointment (including buffer). Admin override required.');
      } else {
        toast.error(typeof d === 'string' ? d : 'Failed to update');
      }
    } finally { setSaving(false); }
  };

  const cancel = async () => {
    if (!appointment?.id) return;
    if (!window.confirm('Cancel this appointment?')) return;
    setSaving(true);
    try {
      await api.delete(`/appointments/${appointment.id}`);
      toast.success('Appointment cancelled');
      // v3.21-1788 · Ghost live-refresh.
      try { window.dispatchEvent(new CustomEvent('ghost:refresh', { detail: { reason: 'appointment_cancelled' } })); } catch (_e) { /* noop */ }
      onUpdated?.({ ...appointment, status: 'cancelled' });
      onOpenChange(false);
    } catch (e) {
      toast.error('Failed to cancel');
    } finally { setSaving(false); }
  };

  const isPending = status === 'pending_outcome';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-testid="appointment-details-modal">
        <DialogHeader>
          <DialogTitle>Appointment Details</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2 space-y-1">
            <div className="flex items-center gap-2">
              <User className="h-3.5 w-3.5 text-slate-500" />
              {/* v3.15-1013: client name + Open Client button. Calendar
                  appointments are bidirectionally linked to the client
                  record — clicking either the name or the button takes
                  the user straight to the client detail page. */}
              {appointment?.client_id ? (
                <button
                  type="button"
                  className="font-semibold text-slate-900 hover:text-blue-700 hover:underline cursor-pointer"
                  onClick={() => { onOpenChange(false); navigate(`/clients/${appointment.client_id}`); }}
                  data-testid="appt-open-client-name"
                  title="Open client record"
                >
                  {appointment?.client_name || '(unknown)'}
                </button>
              ) : (
                <span className="font-semibold">{appointment?.client_name || '(unknown)'}</span>
              )}
              {appointment?.client_id && (
                <button
                  type="button"
                  className="ml-auto inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 hover:underline"
                  onClick={() => { onOpenChange(false); navigate(`/clients/${appointment.client_id}`); }}
                  data-testid="appt-open-client-btn"
                >
                  <ExternalLink className="h-3 w-3" />
                  Open Client
                </button>
              )}
            </div>
            {appointment?.assigned_to_name && (
              <div className="text-xs text-slate-500">with {appointment.assigned_to_name}</div>
            )}
            {whenDisplay && (
              <div className="text-xs text-slate-600 pt-1 flex items-center gap-1.5" data-testid="appt-when-display">
                <Clock className="h-3 w-3 text-slate-400" />
                <span>{whenDisplay}</span>
                <span className="text-slate-400">({tzLabel})</span>
              </div>
            )}
          </div>

          {isPending && (
            <div className="flex items-start gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="appointment-pending-notice">
              <ShieldAlert className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>This appointment has passed its end time. Please log the outcome (Completed / No Show / Rescheduled) to close it out.</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-medium text-slate-600 block mb-1">Starts</label>
              <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} data-testid="appt-starts" />
            </div>
            <div>
              <label className="text-[11px] font-medium text-slate-600 block mb-1">Ends</label>
              <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} data-testid="appt-ends" />
            </div>
          </div>

          <div>
            <label className="text-[11px] font-medium text-slate-600 block mb-1">Status</label>
            <div className="flex flex-wrap gap-1.5" data-testid="appt-status-options">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setStatus(s.value)}
                  className={`text-[11px] px-2.5 py-1 rounded border transition-colors ${status === s.value ? s.bg + ' ring-2 ring-offset-1 ring-current' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  data-testid={`appt-status-${s.value}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] font-medium text-slate-600 block mb-1">Notes</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} data-testid="appt-notes" />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={cancel} disabled={saving} data-testid="appt-cancel-btn">
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Cancel Appointment
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Close</Button>
            <Button type="button" onClick={save} disabled={saving} data-testid="appt-save-btn">
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null} Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
